/**
 * THE COMMISSIONER CONSOLE — one bar, three states, and one irreversible act at a time.
 *
 * From `design_handoff_rules_ia` §2. The handoff's argument is about consequence, not layout, and
 * three rules carry it:
 *
 *   THE IRREVERSIBLE ACTION IS SEPARATED, NOT ADJACENT. It previously sat eight pixels from a
 *   reversible one — the same failure `App.tsx` records about the mock-draft toggle beside the
 *   live board. Reversible things cluster; the one that cannot be undone stands past a divider on
 *   its own.
 *
 *   THE PRIMARY ARMS RATHER THAN ACTS. Pressing it states what it is about to do and waits. The
 *   three acts here publish a docket to ten people, open a meeting, and write the rulebook; none
 *   of them should be reachable by a mis-tap on a phone in a room full of people talking.
 *
 *   THE BAR NAMES ITS ACTOR. There are two commissioners, this bar notifies ten managers, and
 *   `auditLog.ts` exists. "Who published it" is a question that gets asked afterwards.
 *
 * And one thing it says NOT to do: the collapsed bar states the pending obligation, not a count.
 * "3 actions available" answers a question nobody asks — the same objection `App.tsx` raises
 * about a number on a nav item.
 *
 * WHAT THIS MODULE IS. The decision — which act is next, whether it may fire, and every string
 * either state shows. Rendering is `src/ui/CommishConsole.tsx`. Keeping them apart is what lets
 * the arming copy be tested against the thing it claims will happen, which is the whole point of
 * an arming step: a consequence sentence that is out of date is worse than no consequence
 * sentence, because it is believed.
 */

import { managerShort } from './identity.js';
import { LEAGUE_SIZE, RELAXED_MAJORITY, SUPER_MAJORITY } from './proposals.js';
import { isLodged, type ProxyBallot } from './proxy.js';
import { canUnstart, type CommitPlan, type Phase } from './meeting.js';
import type { Docket, RuleProposal } from '../store/types.js';

/** The one state-changing act available now. Null when there is nothing left to do. */
export type ConsoleAct =
  'publish' | 'republish' | 'open-meeting' | 'open-item' | 'record' | 'commit' | null;

export interface Reversible {
  id: 'reorder' | 'attendance' | 'wording' | 'unstart' | 'hold';
  label: string;
}

export interface ConsoleModel {
  act: ConsoleAct;
  /** The item the primary acts on, 1-based, where there is one. */
  itemNo?: number;
  /** Its id, so the caller does not have to re-derive which row the bar meant. */
  itemId?: string;
  /** The primary's label. Never "Save", never "OK" — it names what it writes. */
  primary: string;
  /** The arming question. */
  question: string;
  /** What pressing confirm will actually do, in the present tense. */
  consequence: string;
  /** Where the evening stands, on the open bar. */
  constraint: string;
  /** The pending obligation, on the collapsed bar. Not a count. */
  standing: string;
  /** Why the primary cannot fire. Empty when it can. */
  blocked: string;
  reversible: Reversible[];
}

/** Small numbers read as words in a sentence. Anything above ten is a figure again. */
export function words(n: number): string {
  return ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'][n]
    ?? String(n);
}

const plural = (n: number, one: string, many = `${one}s`) => (n === 1 ? one : many);

export interface ConsoleInput {
  phase: Phase;
  docket: Docket | null | undefined;
  agenda: readonly RuleProposal[];
  proxies: readonly ProxyBallot[];
  plan: CommitPlan;
  /** Carryover re-votes not yet on the docket. Publishing adds them. */
  missing?: number;
  /** Seats represented tonight, for the outcome the arming sentence names. */
  represented?: number;
}

export function consoleModel(input: ConsoleInput): ConsoleModel {
  const { phase, docket, agenda, plan } = input;
  const lodged = input.proxies.filter(isLodged).length;
  const missing = input.missing ?? 0;
  const items = agenda.length + missing;
  const proxyClause = lodged
    ? `${cap(words(lodged))} ${plural(lodged, 'proxy', 'proxies')} `
      + `${plural(lodged, 'is', 'are')} held and will be counted.`
    : 'No proxies are lodged.';

  const reversible: Reversible[] = [
    { id: 'reorder', label: 'Reorder docket' },
    { id: 'attendance', label: 'Attendance' },
  ];

  if (phase === 'committed') {
    return {
      act: null,
      primary: '',
      question: '',
      consequence: '',
      constraint: 'Committed to the record. The rulebook is written and this docket is history.',
      standing: 'Nothing left to do — the record is committed',
      blocked: '',
      reversible: [],
    };
  }

  if (phase === 'writing') {
    return {
      act: 'publish',
      primary: 'Publish the docket',
      question: `Publish the docket to all ${words(LEAGUE_SIZE)} GMs?`,
      consequence: `${cap(words(items))} ${plural(items, 'proposal')} `
        + `${plural(items, 'becomes', 'become')} readable by everyone and submissions close. `
        + 'Wording queries stay private.'
        + (missing ? ` ${cap(words(missing))} carryover re-${plural(missing, 'vote')} `
          + `${plural(missing, 'is', 'are')} added automatically.` : ''),
      constraint: items
        ? `${cap(words(items))} ${plural(items, 'item')} ready. Nothing is visible to the league yet.`
        : 'Nothing is on the docket yet.',
      standing: items
        ? `${cap(words(items))} ${plural(items, 'item')} written · not published`
        : 'Nothing on the docket yet',
      blocked: items ? '' : 'There is nothing to publish.',
      reversible,
    };
  }

  if (phase === 'docket') {
    return {
      act: 'open-meeting',
      primary: 'Open the meeting',
      question: 'Open the meeting now?',
      /*
       * THE ITEM IS NOT NAMED HERE, deliberately. It is the first row of the docket six inches
       * above, and a title inside this sentence turns one clause into three.
       */
      consequence: 'The docket order locks, voting opens on item 1, and '
        + `${words(LEAGUE_SIZE)} GMs are notified. ${proxyClause}`,
      constraint: publishedLine(docket, lodged),
      standing: [
        'Docket locked to GMs',
        lodged ? `${words(lodged)} ${plural(lodged, 'proxy', 'proxies')} lodged` : '',
        'meeting not yet open',
      ].filter(Boolean).join(' · '),
      blocked: agenda.length ? '' : 'The docket is empty.',
      reversible: [...reversible, { id: 'wording', label: 'Suggest wording' }],
    };
  }

  /* phase === 'meeting' */
  const left = agenda.filter((p) => !p.recordedAt).length;
  /*
   * THE WAY BACK OUT OF A MEETING NOBODY MEANT TO OPEN, offered from the bar that opened it.
   *
   * It sits in the reversible cluster because it genuinely is: nothing is lost, and pressing
   * `Open the meeting` again puts it back. And it appears ONLY while it is harmless — the moment
   * the committee closes or records anything, `canUnstart` refuses and the button is not there to
   * be pressed, rather than there and disabled. A control that is present but dead on the one
   * screen where every second is public is worse than one that never appeared.
   */
  const back: Reversible[] = canUnstart(docket, agenda).allowed
    ? [{ id: 'unstart', label: 'Un-start the meeting' }]
    : [];
  /*
   * THE PRIMARY FOLLOWS THE STATE — `design_handoff_docket`, defect 6, and it was the loudest of
   * the six: the bar read `Commit to record…`, truncated, while nothing had been opened and
   * nothing had been heard. A label that is both unreadable and wrong on the one screen where
   * every act is public.
   *
   * Three states inside one evening, and each is a different question:
   *
   *   NOTHING OPEN — put the next item on the floor.
   *   ON THE FLOOR — write down what the room just decided.
   *   EVERYTHING HEARD — publish the night into the rulebook.
   *
   * The third was the only one this bar had, so the other two were being reached from controls
   * inside the rows while the bar sat above them naming an act that could not fire yet.
   */
  const openAt = docket?.openItemId
    ? agenda.findIndex((p) => p.id === docket.openItemId) : -1;

  if (openAt >= 0) {
    const item = agenda[openAt]!;
    const t = countOf(item);
    const need = SUPER_MAJORITY;
    const verdict = t.yes >= need ? 'passed'
      : (!item.isRevote && (input.represented ?? LEAGUE_SIZE) < LEAGUE_SIZE
        && t.yes >= RELAXED_MAJORITY) ? 'passed for one season' : 'failed';
    return {
      act: 'record',
      itemNo: openAt + 1,
      itemId: item.id,
      primary: 'Record the vote',
      question: `Record item ${openAt + 1} as ${verdict}, ${t.yes}–${t.against}?`,
      /*
       * NOT "AND THE ITEM CANNOT BE REOPENED". It can — any commissioner may put it back on the
       * floor (Tim, 27 August) — and an arming sentence that overstates the consequence is worse
       * than none, because it is believed. What closing actually does is stop the voting.
       */
      consequence: [
        t.yes >= need
          ? `It needed ${words(need)} and has ${words(t.yes)}, so it passes permanently.`
          : verdict === 'failed'
            ? `It needed ${words(need)} and has ${words(t.yes)}, so it does not carry.`
            : `It has ${words(t.yes)} with somebody unrepresented, so it carries for one season `
              + 'and returns to next year’s docket.',
        'Votes close. Any commissioner can put it back on the floor.',
      ].join(' '),
      constraint: `Item ${openAt + 1} of ${agenda.length} is on the floor with `
        + `${words(t.yes)} yes. You can record it, or hold it open.`
        + (left === 1 ? ' It is the last one.' : ''),
      standing: `Item ${openAt + 1} on the floor · ${words(t.yes)} yes`,
      blocked: '',
      /*
       * AMENDING VOIDS THE VOTES ON THAT ITEM (Tim, 27 August), which is why it is here rather
       * than on the row: it is a thing to do INSTEAD of recording, at the same moment, and the
       * two belong in one cluster where the choice is visible.
       */
      /*
       * UN-START SURVIVES HERE. A commissioner who opened item 1 by mistake is in exactly this
       * state, and it is the state he needs the way out from (Tim, 26 August). `canUnstart`
       * refuses on its own the moment anything has been decided, so the button is absent rather
       * than present and dead.
       */
      reversible: [
        ...back,
        { id: 'hold', label: 'Hold it open' },
        { id: 'wording', label: 'Amend the wording' },
      ],
    };
  }

  if (left > 0) {
    const nextAt = agenda.findIndex((p) => !p.recordedAt);
    const next = agenda[nextAt]!;
    return {
      act: 'open-item',
      itemNo: nextAt + 1,
      itemId: next.id,
      primary: `Open item ${nextAt + 1}`,
      question: `Put item ${nextAt + 1} on the floor?`,
      consequence: `${cap(words(LEAGUE_SIZE))} screens switch to the item at the same moment and `
        + `voting opens. ${proxyClause}`,
      /*
       * THE ITEM IS NAMED HERE and not in the arming sentence, which is the opposite of the
       * `open-meeting` state above — and for the same reason. Standing still, the question is
       * "which one is next"; mid-arming it is "am I about to do this", and a title inside that
       * sentence turns one clause into three.
       */
      constraint: `Nothing is open. Item ${nextAt + 1} is next: ${next.title}.`,
      standing: `Nothing open · ${words(left)} ${plural(left, 'item')} still to hear`,
      blocked: '',
      reversible: [...back, ...reversible],
    };
  }

  const named = plan.intoRulebook.slice(0, 2)
    .map((id) => agenda.find((p) => p.id === id)?.title ?? id);
  const rest = plan.intoRulebook.length - named.length;

  return {
    act: 'commit',
    primary: 'Publish to the rulebook',
    question: plan.intoRulebook.length
      ? `Write ${words(plan.intoRulebook.length)} `
        + `${plural(plan.intoRulebook.length, 'rule')} into the rulebook?`
      : 'Close the meeting and commit the record?',
    consequence: [
      plan.intoRulebook.length
        ? `${list([...named, ...(rest > 0 ? [`${words(rest)} ${plural(rest, 'other')}`] : [])])} `
          + `${plural(plan.intoRulebook.length, 'gains', 'gain')} a 2026 id, effective immediately.`
        : 'No rule reached the super-majority, so nothing is written into the rulebook.',
      plan.oneSeason.length
        ? `${cap(words(plan.oneSeason.length))} ${plural(plan.oneSeason.length, 'carries', 'carry')} `
          + `for one season and ${plural(plan.oneSeason.length, 'returns', 'return')} to next `
          + 'year’s docket automatically.'
        : '',
      plan.outOfForce.length
        ? `${cap(words(plan.outOfForce.length))} provisional `
          + `${plural(plan.outOfForce.length, 'rule')} `
          + `${plural(plan.outOfForce.length, 'leaves', 'leave')} force tonight.`
        : '',
      'The vote record is attached to each.',
    ].filter(Boolean).join(' '),
    constraint: liveLine(docket, agenda, left),
    standing: left
      ? `${cap(words(left))} ${plural(left, 'item')} still to record`
      : 'Every item recorded · not yet committed',
    blocked: plan.ready ? '' : plan.blocking,
    reversible: [...back, ...reversible],
  };
}

/**
 * `Published 20 August by Tim · two proxies lodged`.
 *
 * DAY THEN MONTH, and a short name rather than a GM id. The date is written the way the rulebook
 * and the minutes write it, and `TIM` is a key in a database — the bar is telling nine other
 * people who did this.
 */
function publishedLine(docket: Docket | null | undefined, lodged: number): string {
  const when = docket?.publishedAt ? dayMonth(docket.publishedAt) : null;
  return [
    when ? `Published ${when}` : 'Published',
    docket?.publishedBy ? `by ${managerShort(docket.publishedBy)}` : '',
    lodged
      ? `· ${words(lodged)} ${plural(lodged, 'proxy', 'proxies')} lodged`
      : '· no proxies lodged',
  ].filter(Boolean).join(' ');
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
  'September', 'October', 'November', 'December'];

/** Fixed, not localised: this string is read beside a rulebook that writes dates one way. */
function dayMonth(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

function liveLine(
  docket: Docket | null | undefined,
  agenda: readonly RuleProposal[],
  left: number,
): string {
  const at = docket?.openItemId ? agenda.findIndex((p) => p.id === docket.openItemId) : -1;
  if (at >= 0) {
    return `Item ${at + 1} of ${agenda.length} open`
      + (left > 1 ? ` · ${words(left - 1)} still to hear` : ' · the last one');
  }
  return left
    ? `Nothing open · ${words(left)} ${plural(left, 'item')} still to hear`
    : 'Every item recorded. Nothing is open.';
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** `a`, `a and b`, `a, b and c`. Two `and`s in one clause is where a sentence stops parsing. */
function list(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}


/**
 * The votes on one item, as the bar states them.
 *
 * AN ABSTENTION IS A NO (2015-I-1), so it belongs on the right of the dash rather than nowhere.
 * `7–3` means seven of ten seats said yes, which is the number the threshold is measured against.
 */
function countOf(p: RuleProposal): { yes: number; against: number } {
  const vals = Object.values(p.votes ?? {});
  const yes = vals.filter((v) => v === 'yes').length;
  return { yes, against: vals.length - yes };
}
